import {
  OverridePrice,
  useCompany,
  useContractTypes,
  useAccessories,
  useProduct,
  useLinkProductToAccessory,
  useUnlinkProductFromAccessory,
} from '@api'
import {FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {errorAtom, getCompanyCurrencyAtom, getUserSelectionAtom, pageLinksAtom, successMessageAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {
  activeCell,
  formatCurrency,
  formatPercentage,
  FormFieldItem,
  generateReadableErrorMessages,
  isCompanyUser,
  unlinkButtonCell,
  renderClickableCell,
  useViewNavigate,
  getUserData,
  generateDropdownOptions,
} from 'common'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

import {ComposedComponents, UIKit} from '@components'

export interface AccessoriesTableData {
  name: string
  price: string
  active: boolean
}

export enum ProductTab {
  GENERAL,
  LINKED_ITEMS,
}

export interface ContractTypePrice {
  contractType: string
  price: number
  residualValue: number
  servicePrice: number
  installationCompensation: number
  override: OverridePrice[]
}

export function useProductViewModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const setError = useSetAtom(errorAtom)
  const setSuccessMessage = useSetAtom(successMessageAtom)
  const [isLinkModalShown, setIsLinkModalShown] = useState<boolean>(false)
  const [accessoryToLink, setAccessoryToLink] = useState<string | undefined>(undefined)
  const [accessoryFormFieldError, setAccessoryFormFieldError] = useState<string>('')
  const [activeTab, setActiveTab] = useState<ProductTab>(ProductTab.GENERAL)
  const navigate = useViewNavigate()
  const {id} = useParams()
  const {t} = useTranslation()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const getCompanyCurrency = useAtomValue(getCompanyCurrencyAtom)
  const currency = userSelection ? getCompanyCurrency(userSelection?.companyId) : 'EUR'

  const {data: productData} = useProduct(id)
  const {data: accessoriesResponse} = useAccessories()
  const {data: contractTypesData} = useContractTypes()
  const {data: companyData} = useCompany(productData?.companyId || '')

  const {mutate: linkProductToAccessoryMutation} = useLinkProductToAccessory()
  const {mutate: unlinkProductFromAccessoryMutation} = useUnlinkProductFromAccessory()

  const accessoriesOptions = useMemo(() => {
    if (!accessoriesResponse) return []
    else
      return generateDropdownOptions(
        accessoriesResponse.filter(item => !productData?.accessories.map(accessory => accessory.id).includes(item.id)),
      )
  }, [accessoriesResponse, productData])

  const accessoriesRows = useMemo(() => {
    if (!productData) return []
    else
      return productData.accessories.map(item => ({
        name: item.name,
        price: formatCurrency(item.price, currency),
        active: item.active,
        id: item.id,
        unlinkId: item.id,
      }))
  }, [productData])

  const contractTypePrices = useMemo(() => {
    if (!productData) return []
    else
      return productData.contractTypePrices.map(item => ({
        contractType: contractTypesData?.find(contractType => contractType.id === item.contractTypeId)?.name || '-',
        price: item.price,
        residualValue: item.residualValue,
        servicePrice: item.servicePrice,
        installationCompensation: item.installationCompensation,
        override: item.override,
      }))
  }, [productData, contractTypesData])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: TranslationKey['Products and accessories'], link: NavigationPathKey.PRODUCT_DASHBOARD},
        {title: TranslationKey['Product Search'], link: NavigationPathKey.PRODUCT_SEARCH},
        {title: t(TranslationKey['Product view'])},
      ]),
    [],
  )

  /**
   * Dynamically generated modal text
   */
  const modalText: string = useMemo(
    () => `${t(TranslationKey['Choose an accessory to link to'])} ${productData?.name}`,
    [productData?.name],
  )

  /**
   * Edit button should be visible only for Administrator users
   */
  const isEditButtonVisible = !isCompanyUser()

  /**
   * Tab buttons configuration
   */
  const tabButtons: ComposedComponents.TabButtonProps<ProductTab>[] = [
    {
      label: t(TranslationKey['General information']),
      key: ProductTab.GENERAL,
      onClick: () => setActiveTab(ProductTab.GENERAL),
    },
    {
      label: t(TranslationKey['Linked accessories']),
      key: ProductTab.LINKED_ITEMS,
      onClick: () => setActiveTab(ProductTab.LINKED_ITEMS),
    },
  ]

  /**
   * Product table columns configuration
   */
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 1.5,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.ACCESSORY_VIEW}/${params.row.id}`)),
    },
    {field: 'price', headerName: t(TranslationKey.Price), flex: 0.5, headerAlign: 'right', align: 'right'},
    activeCell(t),
    ...(isEditButtonVisible ? [unlinkButtonCell(t, id => onUnlink(id))] : []),
  ]

  /**
   * A function that handles unlinking the Product from an Accessory
   * @param accessoryId Accessory ID
   */
  const onUnlink = (accessoryId: string) => {
    if (id)
      unlinkProductFromAccessoryMutation(
        {
          accessoryId,
          productId: id,
        },
        {
          onSuccess: () => {
            setSuccessMessage(t(TranslationKey['Successfully unlinked an accessory']))
          },
        },
      )
  }

  /**
   * A function that handles linking the Product to an Accessory
   * @returns void
   */
  const onLink = () => {
    if (!accessoryToLink) {
      setAccessoryFormFieldError('accessoryToLink')
      setError({
        message: generateReadableErrorMessages(t, ['accessoryToLink']),
        name: '',
      })
      return
    }

    setIsLinkModalShown(false)
    setAccessoryToLink(undefined)
    if (id)
      linkProductToAccessoryMutation(
        {accessoryId: accessoryToLink, productId: id},
        {
          onSuccess: () => {
            setSuccessMessage(t(TranslationKey['Successfully linked an accessory']))
          },
        },
      )
  }

  /**
   * Product form fields configuration - general section
   */
  const productGeneralFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Product number']),
      text: productData?.productNo,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Product name']),
      text: productData?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Company),
      text: companyData?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Active),
      text: productData?.active ? t(TranslationKey.Active) : t(TranslationKey.Inactive),
    },
  ]

  /**
   * Contract Type Price form fields configuration
   * @param index Contract Type Price section index
   * @returns Contrac Type Price form fields
   */
  const productPriceFormFields = (index?: number): FormFieldItem[] => [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Price),
      text: formatCurrency(index !== undefined ? contractTypePrices[index].price : productData?.price || 0, currency),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Residual value']),
      text: `${formatCurrency(index !== undefined ? (contractTypePrices[index].price / 100) * contractTypePrices[index].residualValue : ((productData?.price || 100) / 100) * (productData?.residualValue || 0), currency)} (${formatPercentage(index !== undefined ? contractTypePrices[index].residualValue : productData?.residualValue || 0)})`,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Installation compensation']),
      text: formatCurrency(
        index !== undefined
          ? contractTypePrices[index].installationCompensation
          : productData?.installationCompensation || 0,
        currency,
      ),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Service price']),
      text: formatCurrency(
        index !== undefined ? contractTypePrices[index].servicePrice : productData?.servicePrice || 0,
        currency,
      ),
    },
  ]

  /**
   * Contract Type Override Price form fields configuration
   * @param index Contract Type Override Price section index
   * @returns Contract Type Override Price form fields
   */
  const productOverridePriceFormFields = (index?: number): FormFieldItem[] =>
    index !== undefined
      ? contractTypePrices[index].override?.map(item => ({
          fieldType: FormFieldType.LABEL,
          label: `${item.contractLength} ${t(TranslationKey.Months)}`,
          text: formatCurrency(item.price, currency),
        }))
      : []

  const linkAccessoryFormField: FormFieldItem = {
    fieldType: FormFieldType.SEARCHABLE_DROPDOWN,
    items: accessoriesOptions,
    value: accessoryToLink,
    onChange: (item: UIKit.DropdownItem | null) => {
      setAccessoryToLink(String(item?.value))
      setAccessoryFormFieldError('')
    },
    error: !!accessoryFormFieldError,
    placeholder: t(TranslationKey['Choose an accessory to link to']),
    listboxHeight: 100,
  }

  /**
   * A function that handles the Back button click
   */
  const onPressBack = (): void => {
    navigate(NavigationPathKey.PRODUCT_SEARCH)
  }

  /**
   * A function that handles the Edit button click
   */
  const onPressEdit = (): void => {
    navigate(`${NavigationPathKey.PRODUCT_CREATION}/${id}`)
  }

  /**
   * A function that handles Tab button click
   * @param tabKey Tab key
   */
  const onChangeTab = (tabKey: ProductTab): void => setActiveTab(tabKey)

  /**
   * A function that handles modal close
   */
  const onCloseLinkModal = () => {
    setIsLinkModalShown(false)
    setAccessoryToLink(undefined)
    setAccessoryFormFieldError('')
  }

  /**
   * Create button should only be visible for Administrator users
   */
  const showCreateButton = !isCompanyUser()

  return {
    productGeneralFormFields,
    isEditButtonVisible,
    accessoriesRows,
    columns,
    isLinkModalShown,
    modalText,
    activeTab,
    tabButtons,
    t,
    linkAccessoryFormField,
    contractTypePrices,
    id,
    productPriceFormFields,
    productOverridePriceFormFields,
    onChangeTab,
    onLink,
    onCloseLinkModal,
    setIsLinkModalShown,
    onPressBack,
    onPressEdit,
    navigate,
    showCreateButton,
  }
}
